import { useContext, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircleOutline, Send } from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: WSU_RED },
    "&.Mui-focused fieldset": { borderColor: WSU_RED },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
};

export const SpecialHideFromGal = ({ userId }) => {
  const { userModule, setUserModule } = useContext(UserModuleContext);
  const { authState } = useOktaAuth();

  const [state, setState] = useState({ waiting: false, message: null, error: null, reason: "" });
  const [checked, setChecked] = useState(userModule.userData["Special Hide From GAL"] === true);

  const galStatus = userModule.userData["Special Hide From GAL"];
  const showReasonField = galStatus !== true;

  const updateSpecialHideFromGal = async () => {
    setState((prev) => ({ ...prev, waiting: true }));

    try {
      const res = await fetch(
        `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/special-hide-from-gal`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: authState.accessToken.accessToken,
          },
          body: JSON.stringify({
            userId,
            callerId: authState.accessToken.claims.sub,
            specialHideFromGal: checked,
            reason: state.reason,
          }),
        }
      );

      const data = await res.json();

      if (res.ok) {
        setUserModule((prev) => ({
          ...prev,
          userData: { ...prev.userData, "Special Hide From GAL": checked },
        }));
        setState((prev) => ({ ...prev, message: "Special Hide From GAL updated", waiting: false, error: null }));
      } else {
        setState((prev) => ({ ...prev, message: "Failed to update Special Hide From GAL", error: data, waiting: false }));
      }
    } catch (error) {
      setState((prev) => ({ ...prev, message: "Failed to update Special Hide From GAL", error, waiting: false }));
    }
  };

  if (state.waiting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

      {/* Success / Error Banner */}
      {state.message && !state.error && (
        <Box sx={{ backgroundColor: "#2e7d32", color: "white", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <CheckCircleOutline fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>{state.message}</Typography>
        </Box>
      )}
      {state.error && (
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Update failed</Typography>
          <Typography variant="body2">{state.message}</Typography>
        </Box>
      )}

      {/* ── Section: GAL Setting ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Special Hide From GAL
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{userId}</Typography>
      </Box>

      {galStatus === true && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Please verify with HRS before removing Special Hide From GAL.
          </Typography>
        </Box>
      )}

      <Box sx={{ px: 3, py: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              sx={{ color: WSU_RED, "&.Mui-checked": { color: WSU_RED } }}
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
          }
          label="Special Hide From GAL"
        />
      </Box>

      {showReasonField && (
        <>
          <Divider sx={{ mx: 3 }} />
          <Box sx={{ px: 3, pt: 3, pb: 1 }}>
            <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
              Justification
            </Typography>
          </Box>
          <Box sx={{ px: 3, pb: 3 }}>
            <TextField
              required
              fullWidth
              size="small"
              label="Reason"
              value={state.reason}
              onChange={(e) => setState((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="Provide a reason for this change"
              sx={fieldSx}
            />
          </Box>
        </>
      )}

      {/* ── Actions ── */}
      <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          endIcon={<Send />}
          onClick={updateSpecialHideFromGal}
          disabled={showReasonField && !state.reason.trim()}
          sx={{
            backgroundColor: WSU_RED,
            color: "white",
            px: 4,
            py: 1.25,
            fontWeight: 600,
            letterSpacing: 0.5,
            "&:hover": { backgroundColor: "#8a0c25" },
            "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
          }}
        >
          Update Special Hide From GAL
        </Button>
      </Box>

    </TableContainer>
  );
};
